import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    groupName = "groupName",
    website = "website",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.groupName]: string;
        [fields.website]: string;
    };
}

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
            .tecAttributeLabel {
                color: ${COLORS.secondaryNavyOne};
            }
            .MFAttributeLabel {
                color: ${getColor("primary")};
            }
            .tecAttributeValue {
                color: ${COLORS.secondaryNavyOne};
            }
            .MFAttributeValue {
                color: ${getColor("primary")};
            }
            .attributeValue {
                text-transform: none;
            }
        `,
    },
    attributeTypeLabelText: {
        css: css`
            color: ${COLORS.secondaryNavyOne};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-style: normal;
            letter-spacing: 1.5px;
            text-align: left;
            line-height: ${TYPOGRAPHY.lineHeight.smallestbase};
        `,
    },
    attributeTypeValuesText: {
        css: css`
            opacity: 1;
            color: ${COLORS.secondaryNavyOne};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.light};
            font-style: normal;
            letter-spacing: 1.5px;
            line-height: ${TYPOGRAPHY.lineHeight.smallestbase};
            margin-top: 6px;
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({ product: { detail, brand, productNumber, attributeTypes, properties }, fields,product }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    const [isProductDiscontinued, setIsProductDiscontinued] = React.useState<boolean>(false);
    const [isProductNumberSameWithGlobalProductNumber, setIsProductNumberSameWithGlobalProductNumber] = React.useState<boolean>(false);
   
    function checkDiscontinued(attributeTypes:any) {
        // Iterate through each attribute in the array
        for (let attribute of attributeTypes) {
            // Check if the 'name' property (in lowercase) is 'web_product_status'
            if (attribute.name.toLowerCase() === "web_product_status") {
                // Check if any of the attribute values (in lowercase) have the value "discontinued"
                const isDiscontinued = attribute.attributeValues.some((value:any) => value.value.toLowerCase() === "discontinued");
                
                // Return true if "discontinued" is found, otherwise false
                if (isDiscontinued) {
                    return true;
                }
            }
        }

        // Return false if no match was found
        return false;
    }

    // Function to check if 'GLOBAL_MODEL' exists and if its value matches the given model number
    function checkGlobalModel(attributeTypes: any, modelNumber: string): boolean {
        // Iterate through each attribute in the array
        for (let attribute of attributeTypes) {
            // Check if the 'name' property (in lowercase) is 'global_model'
            if (attribute.name.toLowerCase() === "global_model") {
                // Check if any of the attribute values (in lowercase) match the provided model number (case-insensitive)
                const isModelMatch = attribute.attributeValues.some((value:any) => value.value.toLowerCase() === modelNumber.toLowerCase());
                
                // Return true if model number matches, otherwise false
                if (isModelMatch) {
                    return true;
                }
            }
        }

        // Return false if no match was found
        return false;
    }

    const attributeGroup = properties?.attributeGroup ? JSON.parse(properties.attributeGroup) : {};
    const data = Object.keys(attributeGroup);

    const groupObj: {
        [key: string]: {
            displayName: any;
            values: string[];
        };
    } = {};
    for (const key in attributeGroup) {
        groupObj[key] = attributeGroup[key];
    }
    function removeExactDuplicates(data : any[]) {
        if(product?.properties?.isDiscontinued){
            return data;
        } else {
            const seen = new Set();
            const result = [];

            for (const item of data) {
                const key = JSON.stringify({
                name: item.name,
                label: item.label,
                values: item.attributeValues.map((v:any) => v.value).sort() 
                });

                if (!seen.has(key)) {
                seen.add(key);
                result.push(item);
                }
            }

            return result;
        }
    }
    let limitedAttributeTypes = attributeTypes?.filter(attributeType => attributeType.includeOnProduct) ?? [];
  
    limitedAttributeTypes = removeExactDuplicates(limitedAttributeTypes);

    // Find and remove the object with name 'GLOBAL_MODEL'
    const globalModelIndex = limitedAttributeTypes.findIndex(item => item.name === "GLOBAL_MODEL");

    if (globalModelIndex !== -1 && isProductDiscontinued && !isProductNumberSameWithGlobalProductNumber) {
    const [globalModel] = limitedAttributeTypes.splice(globalModelIndex, 1); // Remove the object
    limitedAttributeTypes.unshift(globalModel); // Add it to the beginning
    }

    const capitializeString = (grpNameArr: string[]) => {
        let name = ``;
        grpNameArr.forEach((item, i) => {
            if (i === 0) {
                name += item.toLowerCase();
            }
            if (i > 0) {
                const capitalizedString: string = item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();
                name += capitalizedString;
            }
        });
        return name;
    };

    const grpNameArr = fields.groupName.split(" ");
    const grpName = capitializeString(grpNameArr);
    const grpToDisplay = attributeGroup[grpName];
    
    React.useEffect(()=>
    {
        setIsProductDiscontinued(checkDiscontinued(attributeTypes)); 
        setIsProductNumberSameWithGlobalProductNumber(checkGlobalModel(attributeTypes, productNumber));
    },[]);
    
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
            {brand && (
                <Typography data-test-selector="brand_item" as="p" {...styles.attributeTypeValuesText}>
                    <Typography
                        data-test-selector="brand_item_label"
                        {...styles.attributeTypeLabelText}
                        className={fields.website === "tecumseh" ? "tecAttributeLabel" : "MFAttributeLabel"}
                    >
                        {translate("Brand")}&nbsp;:&nbsp;
                    </Typography>
                    <span
                        data-test-selector="brand_item_value"
                        className={
                            fields.website === "tecumseh"
                                ? "tecAttributeValue attributeValue"
                                : "MFAttributeValue attributeValue"
                        }
                    >
                        {brand.name}
                    </span>
                </Typography>
            )}
            {grpToDisplay &&
                limitedAttributeTypes.map(attributeType => {
                    if (grpToDisplay.values.indexOf(attributeType.id) === -1) {
                        // if (grpToDisplay.values.indexOf(attributeType.id) === -1 || !grpToDisplay) {
                        return null;
                    }
                    if( isProductDiscontinued && !isProductNumberSameWithGlobalProductNumber && attributeType.name === "GLOBAL_MODEL" ){
                        return (<>
                                <Typography
                                    key={attributeType.id.toString()}
                                    data-test-selector="attributes_item"
                                    data-attributetypeid={attributeType.id}
                                    {...styles.attributeTypeValuesText}
                                    as="p"
                                >
                                    <Typography
                                        data-test-selector="attributes_item_label"
                                        {...styles.attributeTypeLabelText}
                                        className={fields.website === "tecumseh" ? "tecAttributeLabel" : "MFAttributeLabel"}
                                    >
                                        {attributeType.label || attributeType.name}&nbsp;:&nbsp;
                                    </Typography>
                                    <span
                                        data-test-selector="attributes_item_value"
                                        className={
                                            fields.website === "tecumseh"
                                                ? "tecAttributeValue attributeValue"
                                                : "MFAttributeValue attributeValue"
                                        }
                                    >
                                        {(attributeType.attributeValues ?? [])
                                            .map(attributeValue => attributeValue.valueDisplay)
                                            .join(", ")}
                                    </span>
                                </Typography>
                                {detail?.modelNumber && <Typography
                                    key={attributeType.id.toString()+"modelNumber"}
                                    data-test-selector="attributes_item"
                                    {...styles.attributeTypeValuesText}
                                    as="p"
                                >
                                    <Typography
                                        data-test-selector="attributes_item_label"
                                        {...styles.attributeTypeLabelText}
                                        className={fields.website === "tecumseh" ? "tecAttributeLabel" : "MFAttributeLabel"}
                                    >
                                        {translate("Model")}&nbsp;:&nbsp;
                                    </Typography>
                                    <span
                                        data-test-selector="attributes_item_value"
                                        className={
                                            fields.website === "tecumseh"
                                                ? "tecAttributeValue attributeValue"
                                                : "MFAttributeValue attributeValue"
                                        }
                                    >
                                        {detail?.modelNumber}
                                    </span>
                                </Typography>
                                }
                            </>
                        )
                    }
                    
                    if(attributeType.name === "GLOBAL_MODEL" || attributeType.name === "Web_Product_Status" || attributeType.name === "Web_Brand_Family") {
                        return null;
                    }
                    return (
                        <Typography
                            key={attributeType.id.toString()}
                            data-test-selector="attributes_item"
                            data-attributetypeid={attributeType.id}
                            {...styles.attributeTypeValuesText}
                            as="p"
                        >
                            <Typography
                                data-test-selector="attributes_item_label"
                                {...styles.attributeTypeLabelText}
                                className={fields.website === "tecumseh" ? "tecAttributeLabel" : "MFAttributeLabel"}
                            >
                                {attributeType.label || attributeType.name}&nbsp;:&nbsp;
                            </Typography>
                            <span
                                data-test-selector="attributes_item_value"
                                className={
                                    fields.website === "tecumseh"
                                        ? "tecAttributeValue attributeValue"
                                        : "MFAttributeValue attributeValue"
                                }
                            >
                                {(attributeType.attributeValues ?? [])
                                    .map(attributeValue => attributeValue.valueDisplay)
                                    .join(", ")}
                            </span>
                        </Typography>
                    );
                })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.groupName,
                displayName: "Show Group Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.website,
                editorTemplate: "DropDownField",
                defaultValue: "tecumseh",
                fieldType: "General",
                options: [
                    {
                        displayName: "Tecumseh",
                        value: "tecumseh",
                    },
                    {
                        displayName: "Masterflux",
                        value: "masterflux",
                    },
                ],
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
